import { Textarea } from "src/sudokit/components/Textarea";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { forwardRef } from "react";
// @ts-ignore
import styled from "styled-components/macro";
import CharacterGenerationAnimation from "./CharacterGenerationAnimation";
import StyledSelect from "../StyledSelect";

interface GenerateCharacterFormProps {
  showGenerateCharacterForm: boolean;
  setShowGenerateCharacterForm: (show: boolean) => void;
  guidance: string;
  setGuidance: (guidance: string) => void;
  handleGenerateSubmitted: (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleCancel: () => void;
  generatingCharacter: boolean;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  roleOptions: Array<{ value: string; label: string; description?: string }>;
  selectedRole?: string;
  onSelectRole: (role?: string | null) => void;
  roleSelectId?: string;
}

const GenerateCharacterForm = forwardRef(
  (
    {
      showGenerateCharacterForm, //
      guidance,
      setGuidance,
      handleGenerateSubmitted,
      handleCancel,
      generatingCharacter,
      onKeyDown,
      roleOptions = [],
      selectedRole,
      onSelectRole,
      roleSelectId = "generate-character-form-role",
    }: GenerateCharacterFormProps,
    ref
  ) => {
    const selectedRoleOption = roleOptions.find((option) => option.value === selectedRole) || roleOptions[0] || null;

    return (
      <GenerateCharacterFormContainer
        ref={ref}
        $showGenerateCharacterForm={showGenerateCharacterForm}
        aria-hidden={!showGenerateCharacterForm}
        {...(!showGenerateCharacterForm && { inert: "" })}
        $generatingCharacter={generatingCharacter}
      >
        <p className="">Generate Character</p>
        {roleOptions.length > 0 && !generatingCharacter && (
          <div className="role-select">
            <label htmlFor={roleSelectId}>Character Type</label>
            <StyledSelect
              inputId={roleSelectId}
              className="role-selector"
              value={selectedRoleOption}
              options={roleOptions}
              onChange={(option) => onSelectRole(option?.value)}
              placeholder="Select type"
              aria-label="Select the character role template"
            />
          </div>
        )}
        {generatingCharacter && (
          <>
            <div className="generating-character-form-loading">
              <CharacterGenerationAnimation />
            </div>
            <div className="footer">
              <SudoButton
                variant="tertiary"
                goButton={false}
                state="loading"
                loadingPercentage={0}
                className="untouchable-loading-button"
              >
                Generating
              </SudoButton>
            </div>
          </>
        )}
        {!generatingCharacter && (
          <>
            <Textarea
              placeholder="Write custom instructions...&#10;e.g. 'A best friend for my protagonist'"
              mode="default"
              className="text-base"
              value={guidance}
              onChange={(e) => setGuidance(e.target.value)}
              onKeyDown={onKeyDown}
            />
            <div className="footer">
              <SudoButton
                variant="secondaryGrey"
                onClick={handleCancel}
              >
                Cancel
              </SudoButton>
              <SudoButton
                variant="primary"
                goButton
                onClick={handleGenerateSubmitted}
              >
                Go
              </SudoButton>
            </div>
          </>
        )}
      </GenerateCharacterFormContainer>
    );
  }
);

export default GenerateCharacterForm;

type GenerateCharacterFormContainerProps = {
  $showGenerateCharacterForm: boolean;
  $generatingCharacter: boolean;
};

const GenerateCharacterFormContainer = styled.div<GenerateCharacterFormContainerProps>`
  height: ${(props: GenerateCharacterFormContainerProps) => (props.$showGenerateCharacterForm ? "auto" : 0)};
  padding: ${(props: GenerateCharacterFormContainerProps) => (props.$showGenerateCharacterForm ? "10px 14px" : 0)};
  padding-right: ${(props: GenerateCharacterFormContainerProps) => (props.$showGenerateCharacterForm && props.$generatingCharacter ? "0px" : "")};
  margin: ${(props: GenerateCharacterFormContainerProps) => (props.$showGenerateCharacterForm ? "0px 16px" : 0)};
  overflow: ${(props: GenerateCharacterFormContainerProps) => (props.$showGenerateCharacterForm ? "visible" : "hidden")};
  background-color: var(--menu-color);
  border-radius: 20px;
  box-shadow: ${(props: GenerateCharacterFormContainerProps) => (props.$showGenerateCharacterForm ? "var(--tiny-drop-shadow-head-on)" : "none")};
  margin-bottom: 10px;
  position: relative;
  z-index: 1;

  .generating-character-form-loading {
    margin-bottom: -50px;
    margin-left: -14px;
    margin-right: -14px;
  }

  .role-select {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 12px;
    width: min(100%, 220px);

    label {
      font-size: 12px;
      font-weight: 500;
      color: var(--grey-1);
    }

    .role-selector {
      width: 100%;
      min-width: 0;
    }
  }

  .untouchable-loading-button {
    &:hover {
      background: transparent !important;
    }

    &:hover::before,
    &:focus-visible::before {
      background: transparent !important;
      border-radius: 0 !important;
    }
  }

  p {
    color: var(--grey-0);
    margin-bottom: 8px;
    font-family: "Inter", sans-serif;
    font-weight: 500;
    background: transparent;
    border: none;
    padding-left: 0;
    padding-right: 0;
    font-size: 16px;
  }

  .footer {
    padding-right: 15px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
  }
`;
